<template>

  <div class="etx-container Container" :class="paragraphClassList">
    <div class="etx-center Center">

      <div class="etx-flex Flex gv-200 tab-l:gv-0">

        <div class="etx-flex__item Flex__item tab-s:c-10 tab-m:c-9 tab-l:c-4 desk-s:c-3">

          <div class="etx-text Text" :class="entranceClassList">
            <h2 v-if="heading && headingType == 'h2'" class="font-color-primary">{{ heading }}</h2>
            <h3 v-if="heading && headingType == 'h3'" class="font-color-primary">{{ heading }}</h3>
            <h4 v-if="heading && headingType == 'h4'" class="font-color-primary">{{ heading }}</h4>
            <p v-if="headingSubline" class="font-h2 alt">{{ headingSubline }}</p>
            <HelperHtmlRenderer :htmlContent="text" />
          </div>

          <BlokkliProxyMode :isEditing="isEditing" fieldName="field_link_elements_rev" :elements="buttons" />
          <div v-if="buttons.length > 0" class="etx-button-list ButtonList" :class="entranceClassList">
            <Button v-for="button in buttons" :type="button?.type" :targetBlank="button?.targetBlank"
              :href="button?.link?.domainUri?.path || button?.link?.uri?.path" :label="button?.label" :alt="button?.alt" />
          </div>

        </div>

        <div class="etx-flex__item Flex__item tab-l:c-7 desk-s:c-8 tab-l:ml-1">

          <div class="etx-flex Flex mob-m:c-2 tab-m:c-3 gh-50 gv-50 tab-s:gh-100 tab-s:gv-100 av-stretch"
            v-if="competenciesHierarchy?.length > 0">

            <div class="etx-flex__item Flex__item c-12">

              <div class="etx-text Text" :class="entranceClassList">
                <p class="font-h4">{{ $texts('competencies.label', 'Kompetenzen') }}</p>
              </div>

            </div>

            <template v-for="competency in competenciesHierarchy">

              <div class="etx-flex__item Flex__item c-12 Title__row">

                <a class="etx-button Button minimal padding-h-0 color-primary"
                  :class="entranceClassList"
                  :href="competency?.externalLink?.uri?.path ? competency?.externalLink?.uri?.path : competency.link?.url?.path"
                  :target="competency?.externalLink?.uri?.path && '_blank'"
                  :title="competency.name">
                  <div class="etx-button__text Button__text">
                    <span>{{ competency.name }}</span>
                  </div>
                  <div class="etx-button__icon Button__icon">
                    <i class="far" :class="competency?.externalLink?.uri?.path ? 'fa-arrow-up-right-from-square' : 'fa-long-arrow-right'"></i>
                  </div>
                </a>

              </div>

              <div v-for="child in competency.children" class="etx-flex__item Flex__item">

                <div class="etx-card Card CardLink bg-secondary has-link" :class="entranceClassList">
                  <a class="etx-card__link Card__link"
                    :href="child?.externalLink?.uri?.path ? child?.externalLink?.uri?.path : child.link?.url?.path"
                    :target="child?.externalLink?.uri?.path && '_blank'"
                    :title="child.name">
                  </a>

                  <div class="etx-card__item Card__item pv-0 grow-1"></div>

                  <div class="etx-card__item Card__item ph-75 pv-75">

                    <div class="etx-item Item fullwidth av-bottom">
                      <div class="etx-item__text Item__text">
                        <p class="font-size-large font-weight-bold">{{ child.name }}</p>
                      </div>

                      <div class="etx-item__icon Item__icon">

                        <div class="Icon">
                          <i class="far" :class="child?.externalLink?.uri?.path ? 'fa-arrow-up-right-from-square' : 'fa-long-arrow-right'"></i>
                        </div>

                      </div>
                    </div>

                  </div>
                </div>

              </div>

            </template>

          </div>

        </div>

      </div>

    </div>
  </div>

</template>

<script lang="ts" setup>

const props = defineProps<{
  heading?: string
  headingType: string
  headingSubline?: string
  text?: string
  buttons: Array<any>
  competencies: Array<any>
}>()

const { $texts } = useNuxtApp()

const { options, isEditing } = defineBlokkli({
  bundle: 'competencies_listing_manual',
  globalOptions: ['spacingTop', 'backgroundColor'],
  options: {
    spacingBottom: {
      type: 'radios',
      label: 'Unten',
      default: 'pb-50',
      group: 'Innenabstände oben und unten',
      options: {
        'pb-0': '0',
        'pb-25': '25',
        'pb-50': '50',
        'pb-75': '75',
        'pb-100': '100',
        'pb-200': '200',
      },
    },
  }
})

const paragraphClassList = computed(() => {

  const classList = []

  if (options.value.spacingTop) {
    classList.push(options.value.spacingTop)
  }

  if (options.value.spacingBottom) {
    classList.push(options.value.spacingBottom)
  }

  if (options.value.backgroundColor !== 'white') {
    classList.push(options.value.backgroundColor)
  }

  return classList
})

const entranceClassList = computed(() => {
  const classList = []
  if (!isEditing) {
    classList.push('will-appear', 'move-up')
  }
  return classList
})

// Build the taxonomy tree.
function buildHierarchy(terms = []) {

  const termMap = new Map();
  const roots = [];

  // Create a map of terms by id
  terms.forEach(term => {
    termMap.set(term.id, { ...term, children: [] });
  });

  // Build the hierarchy tree
  termMap.forEach(term => {
    if (term.parent.length > 0) {
      term.parent.forEach(parent => {
        if (termMap.has(parent.id)) {
          termMap.get(parent.id).children.push(term);
        }
      });
    } else {
      roots.push(term);
    }
  });

  return roots;
}

const competenciesHierarchy = computed(() => {
  return buildHierarchy(props.competencies)
})

</script>

<style lang="postcss">
.Title__row>a.Button {
  z-index: 1;
}
</style>
